'use client';

import { useCallback, useMemo } from 'react';

import { useTranslations } from 'next-intl';

import BackIcon from '@/assets/icons/back.svg';
import Breadcrumb from '@/components/global/Breadcrumb';
import { Section } from '@/components/shared/Section';
import { H3 } from '@/components/shared/Typography';
import { Button } from '@/components/ui/button';
import { useRouter } from '@/i18n/navigation';
import { cn } from '@/lib/utils/cn';
import { C_Routes } from '@/ts/constants/C_Routes';
import { E_Routes } from '@/ts/enums/E_Routes';

interface I_HistoryHeaderProps {
  title: string;
}

function HistoryHeader({ title }: I_HistoryHeaderProps) {
  const t = useTranslations();
  const tAccessibility = useTranslations('accessibility');
  const router = useRouter();

  // Get titleKey based on variant
  const titleKey = useMemo(() => {
    if (title === 'predictions') {
      return 'profile.sections.predictions.title';
    }
    if (title === 'pools') {
      return 'profile.sections.pools.title';
    }
    if (title === 'referrals') {
      return 'profile.sections.referrals.title';
    }
    return '';
  }, [title]);

  const handleBackClick = useCallback(() => {
    router.back();
  }, [router]);

  return (
    <div className="w-full flex flex-col bg-background gap-3 sticky md:relative top-0 z-10 md:pb-4 md:p-0 md:mb-7">
      <Section className="hidden md:block p-0 md:py-7" container>
        <Breadcrumb
          className="hidden md:block"
          aria-label={tAccessibility('breadcrumb')}
          items={[
            {
              label: t('breadcrumb.home'),
              href: C_Routes[E_Routes.HOME].link
            },
            {
              label: t('breadcrumb.profile'),
              href: C_Routes[E_Routes.PROFILE].link
            },
            { label: t(titleKey) }
          ]}
        />
      </Section>
      <Section
        container
        className={cn(
          'p-7 md:p-0',
          title === 'predictions'
            ? "py-7 px-3 max-sm:mb-0 md:p-14 bg-center bg-no-repeat bg-cover md:bg-[url('/assets/images/account/my-prediction-bg.png')]"
            : 'px-0 md:px-7 max-sm:mb-0'
        )}
      >
        <div className="flex items-center justify-between">
          {title === 'predictions' ? (
            <>
              <Button
                variant="secondary"
                className="hidden md:inline-flex p-2 min-w-9 text-secondary-foreground"
                onClick={handleBackClick}
                icon={
                  <BackIcon className="h-4 w-4 text-secondary-foreground" />
                }
              />
              <Button
                variant="outline"
                className="inline-flex md:hidden p-2 min-w-9 text-secondary-foreground"
                onClick={handleBackClick}
                icon={<BackIcon className="h-4 w-4 text-foreground" />}
              />
            </>
          ) : (
            <Button
              variant="outline"
              className="p-2 min-w-9 text-secondary-foreground"
              onClick={handleBackClick}
              icon={<BackIcon className="h-4 w-4 text-foreground" />}
            />
          )}
          <H3 className="-ml-9">{t(titleKey)}</H3>
          <div />
        </div>
      </Section>
    </div>
  );
}

export default HistoryHeader;
